import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const CartContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate or get session ID
const getSessionId = () => {
  let sessionId = localStorage.getItem('k4u_session');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('k4u_session', sessionId);
  }
  return sessionId;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const sessionId = getSessionId();

  const fetchCart = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cart/${sessionId}`);
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (productIdOrItem, quantity = 1) => {
    setLoading(true);
    try {
      // Check if this is a custom product (object with product data)
      if (typeof productIdOrItem === 'object' && productIdOrItem.product) {
        // Custom product - store in local cart state
        const customItem = {
          product_id: productIdOrItem.product_id,
          quantity: productIdOrItem.quantity || quantity,
          product: productIdOrItem.product
        };
        
        // Check if already in cart
        const existingIndex = cart.items.findIndex(
          item => item.product_id === customItem.product_id
        );
        
        if (existingIndex >= 0) {
          // Update quantity
          const newItems = [...cart.items];
          newItems[existingIndex].quantity += customItem.quantity;
          setCart({ ...cart, items: newItems });
        } else {
          // Add new item
          setCart({ ...cart, items: [...cart.items, customItem] });
        }
        
        setIsOpen(true);
        return;
      }
      
      // Regular product - use API
      await axios.post(`${API}/cart/${sessionId}/add`, {
        product_id: productIdOrItem,
        quantity
      });
      await fetchCart();
      setIsOpen(true);
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, quantity) => {
    setLoading(true);
    try {
      const newItems = cart.items.map(item => {
        if (item.product_id === productId) {
          return { product_id: item.product_id, quantity };
        }
        return { product_id: item.product_id, quantity: item.quantity };
      });
      
      await axios.put(`${API}/cart/${sessionId}/update`, {
        session_id: sessionId,
        items: newItems
      });
      await fetchCart();
    } catch (error) {
      console.error('Error updating cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (productId) => {
    setLoading(true);
    try {
      await axios.delete(`${API}/cart/${sessionId}/item/${productId}`);
      await fetchCart();
    } catch (error) {
      console.error('Error removing from cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API}/cart/${sessionId}`);
      await fetchCart();
    } catch (error) {
      console.error('Error clearing cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const cartTotal = cart.items.reduce((total, item) => {
    if (item.product) {
      const price = item.product.sale_price || item.product.price;
      return total + (price * item.quantity);
    }
    return total;
  }, 0);

  const cartCount = cart.items.reduce((count, item) => count + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      isOpen,
      setIsOpen,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      cartTotal,
      cartCount,
      fetchCart,
      sessionId
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
